import React, { useEffect, useState } from 'react';
import {
  Grid,
  Typography,
  Box,
  Button,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Paper,
  Backdrop,
  CircularProgress,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import { formatDate } from '../../../utils/formatDate';
import TextInput from '../../../sharedModules/form/components/TextInput';
import InspectionRadioGroup from '../components/InspectionRadioGroup';
import { usePost } from '../../../sharedModules/hooks/useQuery';
import axios from '../../../sharedModules/utils/axios.utils';
import {
  conditionOptions,
  conditionWithNaOptions,
  yesNoOptions,
  hvacAgeOptions,
  plumbingAgeOptions,
} from '../config/options';

/** Convert camelCase key to snake_case */
function toSnakeCase(str: string): string {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

/** Build API payload: all form keys converted to snake_case so backend receives expected params */
export function formValuesToPayload(values: typeof initialFormValues): Record<string, unknown> {
  return Object.fromEntries(Object.entries(values).map(([key, value]) => [toSnakeCase(key), value]));
}

export const initialFormValues = {
  // Header
  customerName: '',
  technician: '',
  date: '',
  workOrderNumber: '',

  // HVAC System
  hvacTypeSplitSystem: false,
  hvacTypePackageUnit: false,
  hvacTypeHeatPump: false,
  hvacTypeMiniSplit: false,
  hvacTypeGeothermal: false,
  hvacTypeBoiler: false,
  hvacManufacturer: '',
  hvacAge: '',
  hvacOverallVisualCondition: '',
  hvacOverallOperation: '',
  hvacAirFilterCondition: '',
  hvacIndoorAirQualityNA: false,
  hvacIndoorAirQualityCondition: '',
  hvacOfferedMaintenancePlan: '',
  hvacProperClearance: '',
  hvacServiceRecordUpdated: '',
  hvacNotes: '',

  // Plumbing
  plumbingTypeTankWaterHeater: false,
  plumbingTypeTanklessWaterHeater: false,
  plumbingTypeRecirculationSystem: false,
  plumbingTypeWaterSoftener: false,
  plumbingTypeExpansionTank: false,
  plumbingManufacturer: '',
  plumbingAge: '',
  plumbingOverallVisualCondition: '',
  plumbingExpansionTankNA: false,
  plumbingExpansionTankCondition: '',
  plumbingVisibleLeaksOrRust: '',
  plumbingProperClearance: '',
  plumbingRecommendedAnnualMaintenance: '',
  plumbingInstalledCoupon: '',
  plumbingNotes: '',

  // Electrical
  electricalManufacturer: '',
  electricalOverallVisualCondition: '',
  electricalWholeHouseSurgeProtectionNA: false,
  electricalWholeHouseSurgeProtection: '',
  electricalWiringSecuredProperlyTerminated: '',
  electricalWholeHomeGeneratorNA: false,
  electricalWholeHomeGeneratorCondition: '',
  electricalProperClearance: '',
  electricalNotes: '',

  // Water Treatment
  waterTreatmentNA: false,
  waterTreatmentManufacturer: '',
  waterTreatmentTypeWaterSoftener: false,
  waterTreatmentTypeReverseOsmosis: false,
  waterTreatmentTypeWholeHouseFilter: false,
  waterTreatmentVisibleCondition: '',
  waterTreatmentRecommendedAnnualMaintenance: '',
  waterTreatmentFiltersOrSaltRequired: '',
  waterTreatmentWaterQualityComplaints: '',
  waterTreatmentLocationForSolution: '',
  waterTreatmentNotes: '',
};

export type InspectionFormValues = typeof initialFormValues;

interface InspectionReportFormPageProps {
  homeInspectionJobId?: string;
  prefillData?: Partial<InspectionFormValues>;
  headerDisabled?: boolean;
  title?: string;
  onSubmitSuccess?: () => void;
}

const getAgeColor = (value: string, section: 'hvac' | 'plumbing'): string | undefined => {
  if (!value || value === 'n_a') return undefined;
  const map: Record<string, Record<string, string>> = {
    hvac: { '1_10': '#4caf50', '11_15': '#ff9800', '15_plus': '#f44336' },
    plumbing: { '1_6': '#4caf50', '6_9': '#ff9800', '10_15': '#f44336' },
  };
  return map[section]?.[value];
};

const getAgeCondition = (value: string, section: 'hvac' | 'plumbing'): string => {
  if (!value || value === 'n_a') return '';
  const map: Record<string, Record<string, string>> = {
    hvac: { '1_10': 'good', '11_15': 'needs_attention', '15_plus': 'requires_service' },
    plumbing: { '1_6': 'good', '6_9': 'needs_attention', '10_15': 'requires_service' },
  };
  return map[section]?.[value] || '';
};

const sectionHeaderSx = {
  fontSize: '16px',
  fontWeight: 600,
  color: '#fff',
  bgcolor: '#3d5a80',
  px: 2,
  py: 1,
  borderRadius: '4px 4px 0 0',
};

const subHeaderSx = {
  fontSize: '14px',
  fontWeight: 600,
  color: '#3d5a80',
  bgcolor: '#e8eef4',
  px: 2,
  py: 0.75,
  borderRadius: 1,
  mb: 1.5,
};

interface ManufacturerOption {
  value: string;
  label: string;
}

interface PhotoUploadFieldProps {
  label: string;
  files: File[];
  onChange: (files: File[]) => void;
}

const PhotoUploadField: React.FC<PhotoUploadFieldProps> = ({ label, files, onChange }) => (
  <Box sx={{ mt: 1.5, mb: 1 }}>
    <Button component="label" variant="outlined" size="small" startIcon={<PhotoCameraIcon />}>
      {label}
      <input
        hidden
        type="file"
        accept="image/*"
        multiple
        onChange={(e) => {
          const newFiles = Array.from(e.target.files ?? []);
          onChange([...files, ...newFiles]);
          e.target.value = '';
        }}
      />
    </Button>
    {files.length > 0 && (
      <Box sx={{ mt: 0.5 }}>
        {files.map((f, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {f.name}
            </Typography>
            <IconButton
              size="small"
              onClick={() => onChange(files.filter((_, idx) => idx !== i))}
              sx={{ p: 0.25 }}
            >
              <CloseIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            </IconButton>
          </Box>
        ))}
      </Box>
    )}
  </Box>
);

const InspectionReportFormPage: React.FC<InspectionReportFormPageProps> = ({
  homeInspectionJobId,
  prefillData,
  headerDisabled = false,
  title = 'Home Services Inspection Report',
  onSubmitSuccess,
}) => {
  const [formValues, setFormValues] = useState(initialFormValues);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [hvacPhotos, setHvacPhotos] = useState<File[]>([]);
  const [plumbingPhotos, setPlumbingPhotos] = useState<File[]>([]);
  const [electricalPhotos, setElectricalPhotos] = useState<File[]>([]);
  const [waterTreatmentPhotos, setWaterTreatmentPhotos] = useState<File[]>([]);
  const [hvacManufacturers, setHvacManufacturers] = useState<ManufacturerOption[]>([]);
  const [plumbingManufacturers, setPlumbingManufacturers] = useState<ManufacturerOption[]>([]);
  const [electricalManufacturers, setElectricalManufacturers] = useState<ManufacturerOption[]>([]);
  const [waterTreatmentManufacturers, setWaterTreatmentManufacturers] = useState<ManufacturerOption[]>([]);

  useEffect(() => {
    const fetchManufacturers = async () => {
      try {
        const [hvacRes, plumbingRes, electricalRes, waterRes]: any[] = await Promise.all([
          axios.get('/home_services/manufacturers'),
          axios.get('/home_services/manufacturers'),
          axios.get('/home_services/manufacturers'),
          axios.get('/home_services/manufacturers'),
        ]);
        setHvacManufacturers(hvacRes.data.result || []);
        setPlumbingManufacturers(plumbingRes.data.result || []);
        setElectricalManufacturers(electricalRes.data.result || []);
        setWaterTreatmentManufacturers(waterRes.data.result || []);
      } catch {
        // Silently fail - dropdowns will just be empty
      }
    };
    fetchManufacturers();
  }, []);

  useEffect(() => {
    if (prefillData) {
      setFormValues((prev) => ({ ...prev, ...prefillData }));
    }
  }, [prefillData]);

  const updateField = (field: string, value: any) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const { mutateAsync, isPending, reset, error } = usePost({
    onSuccess: () => {
      if (onSubmitSuccess) {
        onSubmitSuccess();
      } else {
        setSubmitted(true);
        setTimeout(() => setSubmitted(false), 3000);
      }
    },
    onError: (err) => {
      setSubmitError(
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
          'Something went wrong saving the report.',
      );
    },
  });

  const errorMessage =
    submitError ||
    (error && typeof error === 'object' && 'response' in error
      ? (error as { response?: { data?: { errors?: string[]; error?: string } } })?.response?.data?.errors?.[0] ||
        (error as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to update report'
      : null);

  const handleSubmit = async () => {
    setSubmitError(null);
    if (!homeInspectionJobId) return;

    try {
      const payload = formValuesToPayload(formValues);
      const formData = new FormData();

      formData.append('inspection_report[home_inspection_job_id]', homeInspectionJobId);
      Object.entries(payload).forEach(([key, value]) => {
        formData.append(`inspection_report[${key}]`, String(value));
      });

      const photoSections: [string, File[]][] = [
        ['hvac_photos', hvacPhotos],
        ['plumbing_photos', plumbingPhotos],
        ['electrical_photos', electricalPhotos],
        ['water_treatment_photos', waterTreatmentPhotos],
      ];
      photoSections.forEach(([key, files]) => {
        files.forEach((file) => formData.append(`inspection_report[${key}][]`, file));
      });

      await mutateAsync({ url: '/home_services/inspection_reports', data: formData });
    } catch (err: any) {
      const message =
        err?.response?.data?.error || err?.response?.data?.message || 'Something went wrong saving the report.';
      setSubmitError(message);
    }
  };

  return (
    <>
      <Backdrop
        open={isPending}
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <CircularProgress color="inherit" size={48} />
        <Typography variant="h6" color="inherit">
          Submitting report...
        </Typography>
      </Backdrop>
      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
        <Typography variant="h2" sx={{ mb: 2 }}>
          {title}
        </Typography>

        {submitted && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Report updated successfully!
          </Alert>
        )}

        {errorMessage && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => {
              setSubmitError(null);
              reset?.();
            }}
          >
            {errorMessage}
          </Alert>
        )}

        {/* ── Customer Information ── */}
        <Paper variant="outlined" sx={{ mb: 3, overflow: 'hidden' }}>
          <Typography sx={sectionHeaderSx}>Customer Information</Typography>
          <Box sx={{ p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextInput
                  label="Name"
                  value={formValues.customerName}
                  valueChanged={(val) => updateField('customerName', val)}
                  disabled={headerDisabled}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextInput
                  label="Date"
                  value={formatDate(formValues.date)}
                  valueChanged={(val) => updateField('date', val)}
                  disabled={headerDisabled}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextInput
                  label="Technician"
                  value={formValues.technician}
                  valueChanged={(val) => updateField('technician', val)}
                  disabled={headerDisabled}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextInput
                  label="Work Order Number"
                  value={formValues.workOrderNumber}
                  valueChanged={(val) => updateField('workOrderNumber', val)}
                  disabled={headerDisabled}
                />
              </Grid>
            </Grid>
          </Box>
        </Paper>

        {/* ── HVAC System Section ── */}
        <Paper variant="outlined" sx={{ mb: 3, overflow: 'hidden' }}>
          <Typography sx={sectionHeaderSx}>HVAC System</Typography>
          <Box sx={{ p: 2 }}>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth variant="filled">
                  <InputLabel>Manufacturer</InputLabel>
                  <Select
                    value={formValues.hvacManufacturer}
                    onChange={(e) => updateField('hvacManufacturer', e.target.value)}
                  >
                    {hvacManufacturers.map((opt) => (
                      <MenuItem key={opt.value} value={opt.label}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth variant="filled">
                  <InputLabel>Age</InputLabel>
                  <Select
                    value={formValues.hvacAge}
                    onChange={(e) => {
                      const val = e.target.value as string;
                      updateField('hvacAge', val);
                      updateField('hvacAgeCondition', getAgeCondition(val, 'hvac'));
                      (e.target as HTMLInputElement).blur();
                    }}
                    sx={{
                      ...(getAgeColor(formValues.hvacAge, 'hvac') && {
                        bgcolor: getAgeColor(formValues.hvacAge, 'hvac'),
                        color: '#fff',
                        '&.Mui-focused': { bgcolor: getAgeColor(formValues.hvacAge, 'hvac') },
                        '& .MuiSelect-icon': { color: '#fff' },
                        '& .MuiFilledInput-input': { color: '#fff' },
                      }),
                    }}
                  >
                    {hvacAgeOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Typography sx={subHeaderSx}>Type</Typography>
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formValues.hvacTypeSplitSystem}
                    onChange={(e) => updateField('hvacTypeSplitSystem', e.target.checked)}
                  />
                }
                label="Split System"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formValues.hvacTypePackageUnit}
                    onChange={(e) => updateField('hvacTypePackageUnit', e.target.checked)}
                  />
                }
                label="Package Unit"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formValues.hvacTypeHeatPump}
                    onChange={(e) => updateField('hvacTypeHeatPump', e.target.checked)}
                  />
                }
                label="Heat Pump"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formValues.hvacTypeMiniSplit}
                    onChange={(e) => updateField('hvacTypeMiniSplit', e.target.checked)}
                  />
                }
                label="Mini Split / Ductless"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formValues.hvacTypeGeothermal}
                    onChange={(e) => updateField('hvacTypeGeothermal', e.target.checked)}
                  />
                }
                label="Geothermal"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formValues.hvacTypeBoiler}
                    onChange={(e) => updateField('hvacTypeBoiler', e.target.checked)}
                  />
                }
                label="Boiler"
              />
            </Box>

            <Typography sx={subHeaderSx}>Inspection Details</Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12}>
                <InspectionRadioGroup
                  label="Overall/Visual Condition"
                  radioOptions={conditionOptions}
                  value={formValues.hvacOverallVisualCondition}
                  setValue={(val) => updateField('hvacOverallVisualCondition', val)}
                />
              </Grid>
              <Grid item xs={12}>
                <InspectionRadioGroup
                  label="Overall Operation"
                  radioOptions={conditionOptions}
                  value={formValues.hvacOverallOperation}
                  setValue={(val) => updateField('hvacOverallOperation', val)}
                />
              </Grid>
              <Grid item xs={12}>
                <InspectionRadioGroup
                  label="Air Filter Condition"
                  radioOptions={conditionOptions}
                  value={formValues.hvacAirFilterCondition}
                  setValue={(val) => updateField('hvacAirFilterCondition', val)}
                />
              </Grid>
              <Grid item xs={12}>
                <InspectionRadioGroup
                  label="Indoor Air Quality Device"
                  radioOptions={conditionWithNaOptions}
                  value={formValues.hvacIndoorAirQualityCondition}
                  setValue={(val) => updateField('hvacIndoorAirQualityCondition', val)}
                />
              </Grid>
              <Grid item xs={12}>
                <InspectionRadioGroup
                  label="Offered Maintenance Plan"
                  radioOptions={yesNoOptions}
                  value={formValues.hvacOfferedMaintenancePlan}
                  setValue={(val) => updateField('hvacOfferedMaintenancePlan', val)}
                />
              </Grid>
              <Grid item xs={12}>
                <InspectionRadioGroup
                  label="Proper Clearance Around Equipment"
                  radioOptions={yesNoOptions}
                  value={formValues.hvacProperClearance}
                  setValue={(val) => updateField('hvacProperClearance', val)}
                />
              </Grid>
              <Grid item xs={12}>
                <InspectionRadioGroup
                  label="Service Record Updated"
                  radioOptions={yesNoOptions}
                  value={formValues.hvacServiceRecordUpdated}
                  setValue={(val) => updateField('hvacServiceRecordUpdated', val)}
                />
              </Grid>
            </Grid>

            <PhotoUploadField label="Upload HVAC Photos" files={hvacPhotos} onChange={setHvacPhotos} />
            <TextInput
              label="HVAC Notes"
              value={formValues.hvacNotes}
              valueChanged={(val) => updateField('hvacNotes', val)}
              multiline
              rows={3}
            />
          </Box>
        </Paper>

        {/* ── Plumbing Section ── */}
        <Paper variant="outlined" sx={{ mb: 3, overflow: 'hidden' }}>
          <Typography sx={sectionHeaderSx}>Plumbing</Typography>
          <Box sx={{ p: 2 }}>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth variant="filled">
                  <InputLabel>Manufacturer</InputLabel>
                  <Select
                    value={formValues.plumbingManufacturer}
                    onChange={(e) => updateField('plumbingManufacturer', e.target.value)}
                  >
                    {plumbingManufacturers.map((opt) => (
                      <MenuItem key={opt.value} value={opt.label}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth variant="filled">
                  <InputLabel>Age</InputLabel>
                  <Select
                    value={formValues.plumbingAge}
                    onChange={(e) => {
                      const val = e.target.value as string;
                      updateField('plumbingAge', val);
                      updateField('plumbingAgeCondition', getAgeCondition(val, 'plumbing'));
                    }}
                    sx={{
                      ...(getAgeColor(formValues.plumbingAge, 'plumbing') && {
                        bgcolor: getAgeColor(formValues.plumbingAge, 'plumbing'),
                        color: '#fff',
                        '&.Mui-focused': { bgcolor: getAgeColor(formValues.plumbingAge, 'plumbing') },
                        '& .MuiSelect-icon': { color: '#fff' },
                        '& .MuiFilledInput-input': { color: '#fff' },
                      }),
                    }}
                  >
                    {plumbingAgeOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Typography sx={subHeaderSx}>Type</Typography>
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formValues.plumbingTypeTankWaterHeater}
                    onChange={(e) => updateField('plumbingTypeTankWaterHeater', e.target.checked)}
                  />
                }
                label="Tank Water Heater"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formValues.plumbingTypeTanklessWaterHeater}
                    onChange={(e) => updateField('plumbingTypeTanklessWaterHeater', e.target.checked)}
                  />
                }
                label="Tankless Water Heater"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formValues.plumbingTypeRecirculationSystem}
                    onChange={(e) => updateField('plumbingTypeRecirculationSystem', e.target.checked)}
                  />
                }
                label="Recirculation System"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formValues.plumbingTypeWaterSoftener}
                    onChange={(e) => updateField('plumbingTypeWaterSoftener', e.target.checked)}
                  />
                }
                label="Water Softener"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formValues.plumbingTypeExpansionTank}
                    onChange={(e) => updateField('plumbingTypeExpansionTank', e.target.checked)}
                  />
                }
                label="Expansion Tank"
              />
            </Box>

            <Typography sx={subHeaderSx}>Inspection Details</Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12}>
                <InspectionRadioGroup
                  label="Overall/Visual Condition"
                  radioOptions={conditionOptions}
                  value={formValues.plumbingOverallVisualCondition}
                  setValue={(val) => updateField('plumbingOverallVisualCondition', val)}
                />
              </Grid>
              <Grid item xs={12}>
                <InspectionRadioGroup
                  label="Expansion Tank"
                  radioOptions={conditionWithNaOptions}
                  value={formValues.plumbingExpansionTankCondition}
                  setValue={(val) => updateField('plumbingExpansionTankCondition', val)}
                />
              </Grid>
              <Grid item xs={12}>
                <InspectionRadioGroup
                  label="Visible Leaks or Rust"
                  radioOptions={yesNoOptions}
                  value={formValues.plumbingVisibleLeaksOrRust}
                  setValue={(val) => updateField('plumbingVisibleLeaksOrRust', val)}
                />
              </Grid>
              <Grid item xs={12}>
                <InspectionRadioGroup
                  label="Proper Clearance Around Equipment"
                  radioOptions={yesNoOptions}
                  value={formValues.plumbingProperClearance}
                  setValue={(val) => updateField('plumbingProperClearance', val)}
                />
              </Grid>
            </Grid>

            <Typography sx={subHeaderSx}>Tankless Only</Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12}>
                <InspectionRadioGroup
                  label="Recommended Annual Maintenance"
                  radioOptions={yesNoOptions}
                  value={formValues.plumbingRecommendedAnnualMaintenance}
                  setValue={(val) => updateField('plumbingRecommendedAnnualMaintenance', val)}
                />
              </Grid>
              <Grid item xs={12}>
                <InspectionRadioGroup
                  label="Installed $50 Coupon"
                  radioOptions={yesNoOptions}
                  value={formValues.plumbingInstalledCoupon}
                  setValue={(val) => updateField('plumbingInstalledCoupon', val)}
                />
              </Grid>
            </Grid>

            <PhotoUploadField label="Upload Plumbing Photos" files={plumbingPhotos} onChange={setPlumbingPhotos} />
            <TextInput
              label="Plumbing Notes"
              value={formValues.plumbingNotes}
              valueChanged={(val) => updateField('plumbingNotes', val)}
              multiline
              rows={3}
            />
          </Box>
        </Paper>

        {/* ── Electrical Section ── */}
        <Paper variant="outlined" sx={{ mb: 3, overflow: 'hidden' }}>
          <Typography sx={sectionHeaderSx}>Electrical</Typography>
          <Box sx={{ p: 2 }}>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth variant="filled">
                  <InputLabel>Manufacturer</InputLabel>
                  <Select
                    value={formValues.electricalManufacturer}
                    onChange={(e) => updateField('electricalManufacturer', e.target.value)}
                  >
                    {electricalManufacturers.map((opt) => (
                      <MenuItem key={opt.value} value={opt.label}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Typography sx={subHeaderSx}>Inspection Details</Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12}>
                <InspectionRadioGroup
                  label="Overall/Visual Condition"
                  radioOptions={conditionOptions}
                  value={formValues.electricalOverallVisualCondition}
                  setValue={(val) => updateField('electricalOverallVisualCondition', val)}
                />
              </Grid>
              <Grid item xs={12}>
                <InspectionRadioGroup
                  label="Whole House Surge Protection"
                  radioOptions={conditionWithNaOptions}
                  value={formValues.electricalWholeHouseSurgeProtection}
                  setValue={(val) => updateField('electricalWholeHouseSurgeProtection', val)}
                />
              </Grid>
              <Grid item xs={12}>
                <InspectionRadioGroup
                  label="Wiring Secured / Properly Terminated"
                  radioOptions={conditionOptions}
                  value={formValues.electricalWiringSecuredProperlyTerminated}
                  setValue={(val) => updateField('electricalWiringSecuredProperlyTerminated', val)}
                />
              </Grid>
              <Grid item xs={12}>
                <InspectionRadioGroup
                  label="Whole Home Generator"
                  radioOptions={conditionWithNaOptions}
                  value={formValues.electricalWholeHomeGeneratorCondition}
                  setValue={(val) => updateField('electricalWholeHomeGeneratorCondition', val)}
                />
              </Grid>
              <Grid item xs={12}>
                <InspectionRadioGroup
                  label="Proper Clearance Around Equipment (36 in)"
                  radioOptions={yesNoOptions}
                  value={formValues.electricalProperClearance}
                  setValue={(val) => updateField('electricalProperClearance', val)}
                />
              </Grid>
            </Grid>

            <PhotoUploadField
              label="Upload Electrical Photos"
              files={electricalPhotos}
              onChange={setElectricalPhotos}
            />
            <TextInput
              label="Electrical Notes"
              value={formValues.electricalNotes}
              valueChanged={(val) => updateField('electricalNotes', val)}
              multiline
              rows={3}
            />
          </Box>
        </Paper>

        {/* ── Water Treatment Section ── */}
        <Paper variant="outlined" sx={{ mb: 3, overflow: 'hidden' }}>
          <Box sx={{ ...sectionHeaderSx, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#fff' }}>Water Treatment</Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formValues.waterTreatmentNA}
                  onChange={(e) => updateField('waterTreatmentNA', e.target.checked)}
                  sx={{ color: '#fff', '&.Mui-checked': { color: '#fff' } }}
                />
              }
              label={<Typography sx={{ color: '#fff', fontSize: '14px' }}>N/A</Typography>}
            />
          </Box>

          {!formValues.waterTreatmentNA && (
            <Box sx={{ p: 2 }}>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth variant="filled">
                    <InputLabel>Manufacturer</InputLabel>
                    <Select
                      value={formValues.waterTreatmentManufacturer}
                      onChange={(e) => updateField('waterTreatmentManufacturer', e.target.value)}
                    >
                      {waterTreatmentManufacturers.map((opt) => (
                        <MenuItem key={opt.value} value={opt.label}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Typography sx={subHeaderSx}>Type</Typography>
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formValues.waterTreatmentTypeWaterSoftener}
                      onChange={(e) => updateField('waterTreatmentTypeWaterSoftener', e.target.checked)}
                    />
                  }
                  label="Water Softener"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formValues.waterTreatmentTypeReverseOsmosis}
                      onChange={(e) => updateField('waterTreatmentTypeReverseOsmosis', e.target.checked)}
                    />
                  }
                  label="Reverse Osmosis"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formValues.waterTreatmentTypeWholeHouseFilter}
                      onChange={(e) => updateField('waterTreatmentTypeWholeHouseFilter', e.target.checked)}
                    />
                  }
                  label="Whole House Filter"
                />
              </Box>

              <Typography sx={subHeaderSx}>Inspection Details</Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12}>
                  <InspectionRadioGroup
                    label="Visible Condition Of Equipment"
                    radioOptions={conditionOptions}
                    value={formValues.waterTreatmentVisibleCondition}
                    setValue={(val) => updateField('waterTreatmentVisibleCondition', val)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <InspectionRadioGroup
                    label="Recommended Annual Maintenance"
                    radioOptions={yesNoOptions}
                    value={formValues.waterTreatmentRecommendedAnnualMaintenance}
                    setValue={(val) => updateField('waterTreatmentRecommendedAnnualMaintenance', val)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <InspectionRadioGroup
                    label="Are Filters Or Salt Required"
                    radioOptions={yesNoOptions}
                    value={formValues.waterTreatmentFiltersOrSaltRequired}
                    setValue={(val) => updateField('waterTreatmentFiltersOrSaltRequired', val)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <InspectionRadioGroup
                    label="Are There Water Quality Complaints"
                    radioOptions={yesNoOptions}
                    value={formValues.waterTreatmentWaterQualityComplaints}
                    setValue={(val) => updateField('waterTreatmentWaterQualityComplaints', val)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <InspectionRadioGroup
                    label="Location For Water Treatment Solution"
                    radioOptions={yesNoOptions}
                    value={formValues.waterTreatmentLocationForSolution}
                    setValue={(val) => updateField('waterTreatmentLocationForSolution', val)}
                  />
                </Grid>
              </Grid>

              <PhotoUploadField
                label="Upload Water Treatment Photos"
                files={waterTreatmentPhotos}
                onChange={setWaterTreatmentPhotos}
              />
              <TextInput
                label="Water Treatment Notes"
                value={formValues.waterTreatmentNotes}
                valueChanged={(val) => updateField('waterTreatmentNotes', val)}
                multiline
                rows={3}
              />
            </Box>
          )}
        </Paper>

        {/* Submit */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mb: 4 }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={isPending}
            sx={{
              bgcolor: '#3d5a80',
              '&:hover': { bgcolor: '#2c4460' },
              px: 4,
            }}
          >
            {isPending ? 'Saving...' : homeInspectionJobId ? 'Submit Report' : 'Update Report'}
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default InspectionReportFormPage;
